import loadable from "@loadable/component";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { ApplicationTemplate, Backdrop, ContentBox, ContWrapper, TopBarContent } from "./Application.styled";

import CustomHead from "../CustomHead";
import useAppLayout from "./hooks/useAppLayout";
import useCompanySwitchRefresh from "src/hooks/useCompanySwitchRefresh";

import PopupTooltip from "src/components-v2/PopupTooltip";
import Shimmer from "src/components-v2/Shimmer";
import { SpinnerLine } from "src/components-v2/Spinners";
import Steps from "src/components-v2/Steps";
import DepositAutoWithdrawalBanner from "./components/DepositAutoWithdrawalBanner";
import StepsShimmer from "./components/StepsShimmer";

import { APPLICATION_LOCALES, SANDBOX_LOCALES } from "src/consts/locales";
import { useUserInfo } from "src/hooks/user";
import { KYC_STATUS } from "src/scenes/NewVerification/consts/kyc";
import ReKYBBanner from "./components/ReKYBBanner";
import { NOMINATED_RE_KYB } from "./consts/companies";

const CompanyIdsModal = loadable(() => import("./components/CompanyIdsModal"));
const AddNewBusinessModal = loadable(() => import("./components/AddNewBusinessModal"));
const TestModeAlert = loadable(() => import("src/components/TestModeAlert"));
const NewSidebar = loadable(() => import("./components/NewSidebar"));
const TopBar = loadable(() => import("./components/TopBar"));
const UserInfo = loadable(() => import("./components/UserInfo"));
// const ModeDropdown = loadable(() => import("./components/ModeDropdown"));
const OnTrialBanner = loadable(() => import("./components/OnTrialBanner"));
const RequestActivationBanner = loadable(() => import("./components/RequestActivationBanner"));
const TwirlBackground = loadable(() => import("./components/TwirlBackground"));

const propTypes = {
  title: PropTypes.string,
  topBarTitle: PropTypes.any,
  topBarCrumbs: PropTypes.array,
  topBarMenus: PropTypes.array,
  isValidating: PropTypes.bool,
  "data-qaid": PropTypes.string,
  autoCollapsed: PropTypes.bool,
  autoExpand: PropTypes.bool,
  children: PropTypes.node,
  // Already destructured and applied to ContWrapper below — it just was never declared here, so
  // the props inferred for TS callers omitted it and any .tsx page passing it failed to compile.
  contentClass: PropTypes.string,
  // Opt in to a viewport-bounded layout: the template gets a definite height and ContentBox becomes
  // a flex column, so a page can own an internal scroll region instead of growing the window.
  // Off by default — every existing page keeps growing the page and scrolling the window as before.
  fitViewport: PropTypes.bool
};

const defaultProps = {
  title: "",
  topBarTitle: "",
  topBarCrumbs: [],
  autoCollapsed: false,
  autoExpand: false,
  noSidebar: false,
  steps: [],
  loadingSteps: false,
  fitViewport: false
};

function ApplicationLayout(props) {
  useTranslation([...APPLICATION_LOCALES, ...SANDBOX_LOCALES]);
  const { isDoneLoading } = useUserInfo();
  const { state, consts, methods } = useAppLayout(props);

  // Add company switch refresh functionality
  useCompanySwitchRefresh();

  const {
    autoCollapsed,
    title,
    topBarTitle,
    topBarCrumbs,
    noSidebar,
    children,
    isValidating,
    isNewLayout,
    "data-qaid": dataQaid,
    "data-qaid-steps": dataQaidSteps,
    bgMain,
    steps,
    loadingSteps,
    contentClass,
    fitViewport
  } = props;

  const {
    handleToggleClick,
    handleShowCompanyIdsModal,
    handleCloseCompanyIdsModal,
    handleShowAddBusinessModal,
    handleCloseAddBusinessModal
  } = methods;
  const {
    collapsed,
    showTestMode,
    userVerifInfo,
    isMobileWidth,
    companyIds,
    showCompanyIdsModal,
    showAddBusinessModal,
    verifications
  } = state;
  const {
    MENUS,
    PAGE_CONTENT_REF,
    DEPOSIT,
    USER,
    IS_SHOW_BANNER,
    IS_LOADING_USER,
    IS_LOADING_VERIFICATION,
    IS_VERIFICATION_PAGE,
    IS_OWNER,
    NOT_ELIGIBLE_TRIAL,
    IS_OLD_TRIAL,
    IS_LOW_MED_RISK_COMPANY,
    IS_DEMO
  } = consts;

  const isLoadingUserOrVerification = IS_LOADING_USER || IS_LOADING_VERIFICATION;
  const isShowBanner = !isLoadingUserOrVerification && !IS_SHOW_BANNER && !showTestMode && !IS_VERIFICATION_PAGE;
  const isAdjustForTrialInfo =
    !isLoadingUserOrVerification && !IS_SHOW_BANNER && !IS_VERIFICATION_PAGE && !isDoneLoading;
  const hasUser = USER && Object.entries(USER).length > 0;
  const userEmail = hasUser ? USER?.current_user?.email : "";
  const isNewStepper = !!steps.length;

  // condition for nominated companies, show banner to re-KYB
  const isNominatedCompanies = USER?.company?.id && NOMINATED_RE_KYB.includes(`${USER.company.id}`);

  function getCorrectBanner() {
    if ([KYC_STATUS.REQUESTED, KYC_STATUS.NEGOTIATE].includes(USER?.company?.kyc_status) && !IS_LOW_MED_RISK_COMPANY) {
      // Company just requested negotiation
      return <RequestActivationBanner collapsed={collapsed} kycStatus={USER?.company?.kyc_status} />;
    }

    return (
      <OnTrialBanner
        collapsed={collapsed}
        isOwner={IS_OWNER}
        isNotEligible={NOT_ELIGIBLE_TRIAL}
        isOldTrial={IS_OLD_TRIAL}
      />
    );
  }

  return (
    <>
      <PopupTooltip.Wrapper />
      <div className="2xl:u-max-w-screen-2xl 2xl:u-mx-auto 4xl:u-mx-auto 4xl:u-max-w-screen-4xl">
        <CustomHead
          title={title}
          // Don't need this tag since it'll break if user is using mobile device
          // customTag={<meta name="viewport" content="width=1440, initial-scale=1, maximum-scale=1" />}
        />

        {showTestMode && <TestModeAlert collapsed={collapsed} />}

        <ApplicationTemplate
          className={classNames("c-application-layout", fitViewport && "u-h-screen")}
          data-qaid={dataQaid}
        >
          {isNewLayout && <TwirlBackground />}
          <NewSidebar
            className={classNames("c-application-layout__sidebar", noSidebar && "u-hidden")}
            user={USER}
            deposit={DEPOSIT}
            collapsed={collapsed}
            handleToggleClick={handleToggleClick}
            loadingUser={IS_LOADING_USER}
            autoCollapsed={autoCollapsed || isMobileWidth}
            isTestMode={showTestMode}
            menus={MENUS}
            isMobileWidth={isMobileWidth}
            isOwner={IS_OWNER}
            isNotEligible={NOT_ELIGIBLE_TRIAL}
            userVerifInfo={userVerifInfo}
            verifications={verifications}
          />
          <Backdrop collapsed={collapsed} onClick={handleToggleClick} />
          <ContentBox
            className={classNames(
              "c-application-layout__content",
              noSidebar && "u-p-0",
              fitViewport && "u-flex u-flex-col u-min-w-0"
            )}
          >
            {isShowBanner && getCorrectBanner()}
            <TopBar
              className={classNames(
                "u-transition-all u-px-4 md:u-px-8 2xl:u-px-12 xxl:u-rounded-br-lg",
                collapsed && "4xl:u-px-16"
              )}
              title={topBarTitle}
              crumbs={topBarCrumbs}
              adjustForTrialInfo={isAdjustForTrialInfo}
              isTestMode={showTestMode}
              isNewLayout={isNewLayout}
              isMobileWidth={isMobileWidth}
              onClickBarIcon={handleToggleClick}
            >
              <TopBarContent collapsed={collapsed}>
                <UserInfo
                  isMobileWidth={isMobileWidth}
                  handleShowCompanyIdsModal={handleShowCompanyIdsModal}
                  handleShowAddBusinessModal={handleShowAddBusinessModal}
                  companyIds={companyIds}
                />
                {isValidating && <SpinnerLine />}
              </TopBarContent>
            </TopBar>
            <CompanyIdsModal show={showCompanyIdsModal} onClose={handleCloseCompanyIdsModal} companyIds={companyIds} />
            {showAddBusinessModal && (
              <AddNewBusinessModal
                show={showAddBusinessModal}
                onClose={handleCloseAddBusinessModal}
                email={userEmail}
              />
            )}
            {isNewStepper && (
              <div
                className={classNames(
                  "u-bg-main-background u-px-8 u-py-4 u-hidden sm:u-block",
                  "u-transition-all u-px-4 md:u-px-8 2xl:u-px-12",
                  collapsed && "4xl:u-px-16",
                  showTestMode && "u-mt-14"
                )}
              >
                {loadingSteps || !hasUser ? (
                  <StepsShimmer amount={steps?.length} />
                ) : (
                  <Steps isNewDesign steps={steps} isSpreaded={false} data-qaid={dataQaidSteps} />
                )}
              </div>
            )}

            {isNominatedCompanies ? (
              <ReKYBBanner formBusiness={USER?.company?.form_of_business} ownerEmail={USER?.user_owner?.email} />
            ) : (
              <DepositAutoWithdrawalBanner />
            )}

            <ContWrapper
              ref={PAGE_CONTENT_REF}
              className={classNames(
                "c-application-layout__content-wrapper",
                "u-transition-all u-px-4 u-pt-0 md:u-px-8 2xl:u-py-8 2xl:u-px-12",
                collapsed && "4xl:u-px-16",
                !hasUser && "u-pt-7",
                showTestMode && !isNewStepper && "u-mt-14",
                isNewStepper ? "md:u-py-5" : "md:u-py-7",
                contentClass
              )}
              bgMain={bgMain}
            >
              {IS_DEMO ? (
                IS_LOADING_USER || !hasUser ? (
                  <Shimmer />
                ) : (
                  children
                )
              ) : isLoadingUserOrVerification || !hasUser ? (
                <Shimmer height={600} width="100%" className="u-mt-4 md:u-mt-0" />
              ) : (
                children
              )}
            </ContWrapper>
          </ContentBox>
        </ApplicationTemplate>
      </div>
    </>
  );
}

ApplicationLayout.propTypes = propTypes;
ApplicationLayout.defaultProps = defaultProps;

export default ApplicationLayout;
